// Libraries
import { useMemo } from 'react';
// Types
import { AddedResistanceTrend, TimeframeKey } from '@globalTypes/index';
import { ScatterPointVariationKeys, VictoryPointScatter } from '@organisms/index';
import { Resistance } from '@services/index';

/**
 * Represents a function that creates scatter plot data for resistance events.
 *
 * @function
 *
 * @param events - An array of resistance events.
 * @param styleKey - The style key for the scatter points.
 * @param id - The identifier for the scatter data.
 * @returns Scatter plot data in the format suitable for Victory charts.
 */
export const useCreateScatterData = (
  addedResistanceTrendGroup: Record<TimeframeKey, AddedResistanceTrend>,
  timeframe: TimeframeKey,
  events: Resistance[],
  styleKey: ScatterPointVariationKeys,
  id: string
) =>
  useMemo<VictoryPointScatter>(() => {
    const timeframeData = addedResistanceTrendGroup[timeframe];
    const firstTimestamp = new Date(timeframeData[0].timestamp).getTime();
    const lastTimestamp = new Date(timeframeData[timeframeData.length - 1].timestamp).getTime();

    const transformedEvents = events.map(({ timestamp, resistance }) => ({
      x: new Date(timestamp).getTime(),
      y: resistance,
    }));

    const filteredEvents = transformedEvents.filter(
      ({ x: timestamp }) => timestamp >= firstTimestamp && timestamp <= lastTimestamp
    );

    const scatterData: VictoryPointScatter = {
      data: filteredEvents,
      styleKey,
      id,
    };

    return scatterData;
  }, [addedResistanceTrendGroup, timeframe, events, styleKey, id]);
